import { useCallback, useEffect, useMemo, useState } from 'react';
import { Gear, PencilSimple, PlusCircle, Tag, X } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import { Id, Mono } from '../../components/ui/Mono';
import Select from '../../components/ui/Select';
import { Skeleton, SkeletonTable } from '../../components/ui/Skeleton';
import StandardTable from '../../components/ui/StandardTable';
import { useToast } from '../../components/ui/Toast';
import {
    AREA_CHIP_ALL,
    AREA_CHIP_VIRTUAL,
    BuildAreaChips,
    BuildAreaContext,
    LocationMatchesArea,
    LockedAreaForUser,
} from '../../components/warehouse/areaFilter';
import BulkLocationEditModal from '../../components/warehouse/BulkLocationEditModal';
import LocationCreateModal from '../../components/warehouse/LocationCreateModal';
import LocationEditModal from '../../components/warehouse/LocationEditModal';
import LocationInfoPanel from '../../components/warehouse/LocationInfoPanel';
import LocationLabelsModal from '../../components/warehouse/LocationLabelsModal';
import LocationsCascade from '../../components/warehouse/LocationsCascade';
import {
    BuildLocationStatusOptions,
    BuildLocationTypeOptions,
    FormatBayWidth,
    FormatWeightKg,
    OccupancyBadgeVariant,
    StatusBadgeVariant,
} from '../../components/warehouse/locationFields';
import { IsVirtualLocation } from '../../components/warehouse/locationsTree';
import { NormalizeLayout } from '../../components/warehouse/map/mapModel';
import WarehouseMap from '../../components/warehouse/map/WarehouseMap';
import { ExtractListRows, ParseLocationsMapRows } from '../../components/warehouse/payload';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { VIRTUAL_LOCATION_TYPE } from '../../constants/warehouseLocations';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { ExtractPaging } from '../../utils/aggregation';
import { FormatInt } from '../../utils/format';

// Escala do M1 (alguns racks + fixas ≈ centenas de posições): uma página só cobre;
// teto do wire é 5000 (rota valida; pedir acima disso é 400, não "capado") —
// acima do teto o aviso de lista truncada aparece sobre a listagem.
const LIST_LIMIT = 5000;

const BLANK_CELL = <span className="text-mono-400">—</span>;

function toFinite(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function renderNullableValue(value) {
    return value === null || value === undefined || value === '' ? BLANK_CELL : <Mono>{String(value)}</Mono>;
}

// O mapa cabe INTEIRO na área útil (sem rolagem): teto de altura derivado do
// viewport, entregue ao WarehouseMap via `fitHeight` (contrato r4).
function computeMapFitHeight() {
    if (typeof window === 'undefined') {
        return 480;
    }
    return Math.min(680, Math.max(360, Math.round(window.innerHeight - 320)));
}

/**
 * /warehouse/locations — Localizações do Armazém (r4, UNIFICADA).
 *
 * Princípio do dono (4ª rodada): localização física DEVE estar posicionada no
 * mapa — mapa e dados são UMA coisa. A página tem DOIS filtros (Área em chips
 * e o MODO Tabela | Cascata | Mapa — uma visualização por vez, nada de mapa
 * em cima + tabela embaixo) e DUAS colunas: o modo escolhido à esquerda e o
 * DRAWER PERMANENTE à direita (vazio até clicar numa posição em qualquer
 * modo). Fluxos que criam física passam pelo mapa: nova localização física →
 * placeRequest {type:'fixed'} (clique na célula cria). Criar RACK NÃO mora
 * aqui: vive só no Editar galpão — a página só recarrega os dados quando o
 * mapa avisa (onRackCreated). NÃO EXISTE MAIS RUA (02/09/2026): cada
 * porta-pallet é um rack (`A`, `B`…) com blocos sequenciais; filtros,
 * colunas e seleção falam rack/bloco. SEM DELETE — indisponível é
 * BLOCKED/MAINTENANCE. Wire snake_case.
 */
const WarehouseLocationsPage = () => {
    const { label } = useWarehouseLabels();
    const { user, checkPermission } = useAuth();
    const toast = useToast();
    const [filters, setFilters] = useState({ rack: '', type: '', status: '' });
    const [areaChip, setAreaChip] = useState(AREA_CHIP_ALL);
    const [view, setView] = useState('table');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [pagingTotal, setPagingTotal] = useState(0);
    const [mapRows, setMapRows] = useState([]);
    const [mapError, setMapError] = useState(null);
    const [mapLayout, setMapLayout] = useState(null);
    const [mapLayoutLoading, setMapLayoutLoading] = useState(true);
    const [mapLayoutError, setMapLayoutError] = useState(null);
    const [mapFitHeight, setMapFitHeight] = useState(computeMapFitHeight);
    const [mapEditMode, setMapEditMode] = useState(false);
    // Seleção PERSISTENTE do mapa ({kind:'block'|'fixed'|'rack', ...}) — o
    // mapa marca "onde estou vendo"; o drawer mostra o conteúdo.
    const [selection, setSelection] = useState(null);
    const [panelTarget, setPanelTarget] = useState(null);
    // Posicionamento da física individual (contrato r4 do WarehouseMap):
    // guarda o payload até a célula ser clicada.
    const [placeRequest, setPlaceRequest] = useState(null);
    const [pendingCreate, setPendingCreate] = useState(null);
    const [isBulkOpen, setBulkOpen] = useState(false);
    const [isCreateOpen, setCreateOpen] = useState(false);
    const [isLabelsOpen, setLabelsOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);

    const canEditMap = checkPermission('wms:manage:locations');

    // Filtros alimentam o GET (wire snake_case). O chip de área também: chip
    // Virtuais → ?virtual=true; chip de área física → ?virtual=false (o recorte
    // por área é client-side via layout, que é quem conhece o vínculo).
    const loadLocations = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const params = { limit: LIST_LIMIT, offset: 0 };
            if (areaChip === AREA_CHIP_VIRTUAL) {
                params.virtual = 'true';
            } else if (areaChip !== AREA_CHIP_ALL) {
                params.virtual = 'false';
            }
            if (filters.rack) {
                params.rack = filters.rack;
            }
            if (filters.type) {
                params.type = filters.type;
            }
            if (filters.status) {
                params.status = filters.status;
            }
            const data = await api.getWarehouseLocations(params);
            const listRows = ExtractListRows(data, ['locations']);
            setRows(listRows);
            setPagingTotal(ExtractPaging(data, { total: listRows.length }).total);
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
            setPagingTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filters, areaChip]);

    // Resumo por rack/bloco (GET /locations/map) — overlay de status do mapa.
    const loadMap = useCallback(async () => {
        setMapError(null);
        try {
            const data = await api.getWarehouseLocationsMap();
            setMapRows(ParseLocationsMapRows(data));
        } catch (error) {
            setMapError(error?.message || 'Erro ao carregar');
            setMapRows([]);
        }
    }, []);

    // Layout espacial do mapa (1 JSON por org): null = ainda não desenhado.
    const loadMapLayout = useCallback(async () => {
        setMapLayoutLoading(true);
        setMapLayoutError(null);
        try {
            const data = await api.getWarehouseMapLayout();
            setMapLayout(NormalizeLayout(data?.layout));
        } catch (error) {
            setMapLayoutError(error?.message || 'Erro ao carregar');
            setMapLayout(null);
        } finally {
            setMapLayoutLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLocations();
    }, [loadLocations]);

    useEffect(() => {
        loadMap();
    }, [loadMap]);

    useEffect(() => {
        loadMapLayout();
    }, [loadMapLayout]);

    // Mapa inteiro na área útil também depois de redimensionar a janela.
    useEffect(() => {
        const onResize = () => setMapFitHeight(computeMapFitHeight());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Criar / editar / bulk (e o rack criado no editor) mudam listagem E
    // overlay — recarrega os dois.
    const reloadAll = useCallback(() => {
        loadLocations();
        loadMap();
    }, [loadLocations, loadMap]);

    // ── Filtro 1: ÁREA (chips Todas / Virtuais / áreas do layout) ───────────
    const areaChips = useMemo(() => BuildAreaChips(mapLayout, label), [mapLayout, label]);

    // Trava por permissão: papéis restritos a UMA área chegam no M4/M5 — quando
    // LockedAreaForUser devolver a área, o chip dela nasce selecionado e TODOS
    // os chips desabilitam (a UI já respeita o shape desde já).
    const lockedArea = useMemo(() => LockedAreaForUser(user?.permissions), [user?.permissions]);
    const chipsLocked = Boolean(lockedArea);
    useEffect(() => {
        if (!lockedArea) {
            return;
        }
        const chip = areaChips.find(
            (candidate) => candidate.id === String(lockedArea) || candidate.areaType === lockedArea,
        );
        if (chip && areaChip !== chip.id) {
            setAreaChip(chip.id);
        }
    }, [lockedArea, areaChips, areaChip]);

    // Chip selecionado sumiu do layout (área apagada no editor) → volta a Todas.
    useEffect(() => {
        if (!areaChips.some((chip) => chip.id === areaChip)) {
            setAreaChip(AREA_CHIP_ALL);
        }
    }, [areaChips, areaChip]);

    const selectedAreaChip = areaChips.find((chip) => chip.id === areaChip) || null;
    const selectedAreaId =
        selectedAreaChip?.areaId !== null && selectedAreaChip?.areaId !== undefined ? selectedAreaChip.areaId : null;
    const areaContext = useMemo(
        () => (selectedAreaId !== null ? BuildAreaContext(mapLayout, selectedAreaId) : null),
        [mapLayout, selectedAreaId],
    );

    // Linhas visíveis = GET (rack/type/status/virtual) + recorte de área.
    const visibleRows = useMemo(() => {
        if (areaChip === AREA_CHIP_VIRTUAL) {
            return rows.filter((row) => IsVirtualLocation(row));
        }
        if (!areaContext) {
            return rows;
        }
        return rows.filter((row) => LocationMatchesArea(row, areaContext));
    }, [rows, areaChip, areaContext]);

    // ── Seleção ↔ drawer (posição clicada em QUALQUER modo) ─────────────────
    const clearSelection = useCallback(() => {
        setPanelTarget(null);
        setSelection(null);
    }, []);

    // Linha/posição clicada (Tabela, Cascata, lista do drawer): preenche o
    // drawer e sincroniza a seleção do mapa (trocar para o modo Mapa mostra
    // "onde estou vendo").
    const selectLocationRow = useCallback((row) => {
        setPanelTarget({ kind: 'location', location: row });
        if (IsVirtualLocation(row)) {
            setSelection(null);
            return;
        }
        const rack = row?.rack ? String(row.rack).toUpperCase() : null;
        const bay = toFinite(row?.bay);
        if (rack && bay !== null) {
            setSelection({ kind: 'block', rack, bay });
        } else if (rack) {
            setSelection({ kind: 'rack', rack });
        } else if (row?.code) {
            setSelection({ kind: 'fixed', code: row.code });
        } else {
            setSelection(null);
        }
    }, []);

    // Clique no mapa (contrato r4): bloco/fixa/rack viram alvo do drawer; célula
    // vazia limpa a seleção.
    const handleMapSelect = useCallback(
        (picked) => {
            if (!picked || picked.kind === 'cell') {
                clearSelection();
                return;
            }
            setSelection(picked);
            if (picked.kind === 'block') {
                setPanelTarget({ kind: 'block', rack: picked.rack, bay: picked.bay });
            } else if (picked.kind === 'rack') {
                setPanelTarget({ kind: 'rack', rack: picked.rack });
            } else if (picked.kind === 'fixed') {
                const row = rows.find((candidate) => candidate?.code === picked.code);
                setPanelTarget(row ? { kind: 'location', location: row } : { kind: 'code', code: picked.code });
            }
        },
        [rows, clearSelection],
    );

    // ── Posicionamento obrigatório da nova localização física ───────────────
    // Nova localização FÍSICA: o modal validou e entregou o payload; a página
    // troca para o modo Mapa e pede a célula. O POST só acontece no resolve.
    const handleRequestPlacement = useCallback(({ payload, code }) => {
        setPendingCreate({ payload, code });
        setPlaceRequest({ type: 'fixed', code });
        setAreaChip((chip) => (chip === AREA_CHIP_VIRTUAL ? AREA_CHIP_ALL : chip));
        setView('map');
    }, []);

    // Resolve do posicionamento (onPlaceResolved do mapa E o botão Cancelar da
    // página): fixa criada SÓ depois da célula clicada; cancelar NÃO cria.
    const resolvePlacement = useCallback(
        async (ok) => {
            const pending = pendingCreate;
            if (!placeRequest) {
                return;
            }
            setPlaceRequest(null);
            setPendingCreate(null);
            if (!pending) {
                return;
            }
            if (!ok) {
                toast.info(label('wms.place.cancelled_not_created'));
                return;
            }
            try {
                await api.createWarehouseLocation(pending.payload);
                toast.success(label('wms.place.created_and_placed'));
            } catch (error) {
                const serverMessage =
                    error?.message && error.message !== 'Unknown error' ? error.message : 'Erro ao salvar';
                toast.error(`${serverMessage} — ${label('wms.place.create_failed_after_place')}`);
            }
            // O mapa já salvou a célula em layout.fixed — sincroniza o layout
            // junto com a listagem.
            reloadAll();
            loadMapLayout();
        },
        [placeRequest, pendingCreate, toast, label, reloadAll, loadMapLayout],
    );

    // Rack criado (ou posicionado) DENTRO do Editar galpão (generate-rack real +
    // layout salvo lá): recarrega dados reais; o layout novo já chegou via
    // onLayoutChange.
    const handleRackCreated = useCallback(() => {
        reloadAll();
    }, [reloadAll]);

    // ── Edição inline do galpão (botão liga mode="edit" ali mesmo) ──────────
    const toggleMapEdit = useCallback(() => {
        if (mapEditMode) {
            setMapEditMode(false);
            // Volta à verdade SALVA (o salvar com re-senha é do mapa; concluir
            // sem salvar descarta).
            loadMapLayout();
        } else {
            setMapEditMode(true);
            setView('map');
            setAreaChip((chip) => (chip === AREA_CHIP_VIRTUAL ? AREA_CHIP_ALL : chip));
        }
    }, [mapEditMode, loadMapLayout]);

    // Universo de racks (letras) para filtro e modais: o resumo do mapa dá a
    // lista completa; a união com as linhas carregadas cobre o resumo ainda não
    // lido. NÃO EXISTE MAIS RUA: cada porta-pallet é um rack.
    const rackOptions = useMemo(() => {
        const racks = new Set();
        for (const row of mapRows) {
            if (row?.rack) {
                racks.add(String(row.rack).toUpperCase());
            }
        }
        for (const row of rows) {
            if (row?.rack) {
                racks.add(String(row.rack).toUpperCase());
            }
        }
        return [...racks].sort((a, b) => a.localeCompare(b));
    }, [mapRows, rows]);

    const setFilter = (name, value) => {
        setFilters((previous) => ({ ...previous, [name]: value }));
    };

    // getValue devolve o TEXTO exibido (rótulo PT) — busca e export operam
    // sobre o que o usuário vê. Códigos e medidas de máscara fixa em mono.
    // r4: tabela SIMPLES (sem grupos, sem coluna de mapa/ações) — clique na
    // linha preenche o drawer; editar fica no drawer.
    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.field.code'),
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} className="font-medium" />,
            },
            {
                key: 'type',
                label: label('wms.field.type'),
                getValue: (row) => label(`wms.location_type.${row.type}`),
                render: (row) => <Badge>{label(`wms.location_type.${row.type}`)}</Badge>,
            },
            {
                key: 'rack',
                label: label('wms.field.rack'),
                align: 'center',
                getValue: (row) => row.rack ?? '',
                render: (row) => renderNullableValue(row.rack),
            },
            {
                key: 'bay',
                label: label('wms.field.bay'),
                align: 'center',
                getValue: (row) => row.bay ?? '',
                render: (row) => renderNullableValue(row.bay),
            },
            {
                key: 'level',
                label: label('wms.field.level'),
                align: 'center',
                getValue: (row) => row.level ?? '',
                render: (row) => renderNullableValue(row.level),
            },
            {
                key: 'position',
                label: label('wms.field.position'),
                align: 'center',
                getValue: (row) => row.position ?? '',
                render: (row) => renderNullableValue(row.position),
            },
            {
                // Largura do vão é do BLOCO (wms_blocks) — o DTO da posição
                // espelha o valor do bloco dela.
                key: 'bay_width',
                label: label('wms.field.bay_width'),
                align: 'right',
                getValue: (row) => row.bay_width ?? '',
                render: (row) => {
                    const text = FormatBayWidth(row.bay_width);
                    return text ? <Mono>{text}</Mono> : BLANK_CELL;
                },
            },
            {
                key: 'max_weight_kg',
                label: label('wms.field.max_weight_kg'),
                align: 'right',
                getValue: (row) => row.max_weight_kg ?? '',
                render: (row) => {
                    const text = FormatWeightKg(row.max_weight_kg);
                    return text ? <Mono>{text}</Mono> : BLANK_CELL;
                },
            },
            {
                key: 'status',
                label: label('wms.field.status'),
                getValue: (row) => label(`wms.location_status.${row.status}`),
                render: (row) => (
                    <Badge variant={StatusBadgeVariant(row.status)}>
                        {label(`wms.location_status.${row.status}`)}
                    </Badge>
                ),
            },
            {
                key: 'occupancy',
                label: label('wms.field.occupancy'),
                getValue: (row) => label(`wms.occupancy.${row.occupancy || 'FREE'}`),
                render: (row) => (
                    <Badge variant={OccupancyBadgeVariant(row.occupancy || 'FREE')}>
                        {label(`wms.occupancy.${row.occupancy || 'FREE'}`)}
                    </Badge>
                ),
            },
        ],
        [label],
    );

    const truncatedNotice =
        pagingTotal > rows.length ? (
            <p className="text-xs text-warning-text dark:text-warning-300">
                {`${FormatInt(rows.length)} de ${FormatInt(pagingTotal)} — ${label('wms.locations.partial_list')}`}
            </p>
        ) : null;

    // ── Filtros da página: chips de ÁREA + toggle de MODO ───────────────────
    const areaChipButtons = (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                {label('wms.locations.area_filter')}
            </span>
            {areaChips.map((chip) => {
                const active = areaChip === chip.id;
                return (
                    <button
                        key={chip.id}
                        type="button"
                        disabled={chipsLocked}
                        onClick={() => setAreaChip(chip.id)}
                        aria-pressed={active}
                        title={chipsLocked && active ? label('wms.locations.area_locked') : undefined}
                        className={`rounded-full border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                            active
                                ? 'border-transparent bg-black font-medium text-white dark:bg-white dark:text-black'
                                : 'border-mono-300 text-mono-700 hover:bg-mono-50 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900'
                        }`}
                    >
                        {chip.text}
                    </button>
                );
            })}
        </div>
    );

    // Filtro 2: modo — MESMO componente de alternância da rodada anterior
    // (botões com aria-pressed), agora com três opções.
    const viewToggle = (
        <div className="inline-flex shrink-0 rounded-lg border border-mono-300 p-0.5 dark:border-mono-700">
            {[
                { id: 'table', text: label('wms.locations.mode_table') },
                { id: 'cascade', text: label('wms.locations.mode_cascade') },
                { id: 'map', text: label('wms.locations.mode_map') },
            ].map((option) => (
                <button
                    key={option.id}
                    type="button"
                    onClick={() => setView(option.id)}
                    aria-pressed={view === option.id}
                    className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                        view === option.id
                            ? 'bg-black font-medium text-white dark:bg-white dark:text-black'
                            : 'text-mono-600 hover:bg-mono-100 dark:text-mono-400 dark:hover:bg-mono-900'
                    }`}
                >
                    {option.text}
                </button>
            ))}
        </div>
    );

    // ── Aviso de posicionamento ativo (fixa aguardando a célula do mapa) ────
    const placementBanner = placeRequest ? (
        <div className="flex flex-col gap-2 rounded-xl border border-link-300 bg-link-50 p-3 dark:border-link-900 dark:bg-link-900/20 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0 text-sm text-link-700 dark:text-link-300">
                <span className="font-semibold">{label('wms.place.fixed_banner')}</span>{' '}
                <Id value={placeRequest.code} className="text-sm" />
                <span className="mt-0.5 block text-xs opacity-90">{label('wms.place.fixed_hint')}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => resolvePlacement(false)} className="shrink-0">
                <X size={14} className="mr-1.5" />
                {label('wms.place.cancel')}
            </Button>
        </div>
    ) : null;

    // ── Conteúdo principal por MODO ──────────────────────────────────────────
    const tableContent = loading ? (
        <SkeletonTable columns={10} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.locations.title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={loadLocations}
        />
    ) : (
        <StandardTable
            rows={visibleRows}
            columns={columns}
            rowKey={(row, index) => row.id || index}
            onRowClick={selectLocationRow}
            exportFileName="wms-localizacoes"
            countLabel="localizações"
            emptyMessage={label('wms.cascade.empty')}
        />
    );

    const cascadeContent = loading ? (
        <SkeletonTable columns={3} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.locations.title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={loadLocations}
        />
    ) : (
        <LocationsCascade rows={visibleRows} label={label} onSelectLocation={selectLocationRow} />
    );

    const mapUnavailable = areaChip === AREA_CHIP_VIRTUAL && !placeRequest && !mapEditMode;
    const mapContent = (
        <section className="space-y-3 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold text-black dark:text-white">{label('wms.map.title')}</h2>
                    <Badge variant="neutral">{label('wms.map.orientation')}</Badge>
                </div>
                {canEditMap ? (
                    <Button variant={mapEditMode ? 'primary' : 'secondary'} size="sm" onClick={toggleMapEdit}>
                        <Gear size={16} className="mr-2" />
                        {mapEditMode ? label('wms.map.finish_edit') : label('wms.map.edit_warehouse')}
                    </Button>
                ) : null}
            </div>
            {mapEditMode ? (
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.edit_inline_hint')}</p>
            ) : null}

            {mapUnavailable ? (
                <p className="rounded-lg border border-dashed border-mono-300 px-4 py-6 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
                    {label('wms.map.virtual_filter_note')}
                </p>
            ) : mapLayoutLoading ? (
                <Skeleton className="h-48 w-full" />
            ) : mapLayoutError ? (
                <div className="space-y-2 py-4 text-center">
                    <p className="text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.common.load_error')} {mapLayoutError}
                    </p>
                    <div className="flex justify-center">
                        <Button variant="secondary" size="sm" onClick={loadMapLayout}>
                            {label('wms.common.retry')}
                        </Button>
                    </div>
                </div>
            ) : !mapLayout && !mapEditMode && !placeRequest ? (
                <div className="space-y-3 py-8 text-center">
                    <p className="text-sm font-medium text-black dark:text-white">
                        {label('wms.map.not_configured')}
                    </p>
                    <p className="mx-auto max-w-xl text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.map.not_configured_r4')}
                    </p>
                    {canEditMap ? (
                        <div className="flex justify-center">
                            <Button variant="secondary" onClick={toggleMapEdit}>
                                <Gear size={18} className="mr-2" />
                                {label('wms.map.edit_warehouse')}
                            </Button>
                        </div>
                    ) : null}
                </div>
            ) : (
                <>
                    {mapError ? (
                        <div className="flex items-center justify-between gap-3 rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-text dark:bg-warning-900/30 dark:text-warning-300">
                            <span>
                                {label('wms.common.load_error')} {mapError}
                            </span>
                            <Button variant="ghost" size="sm" onClick={loadMap}>
                                {label('wms.common.retry')}
                            </Button>
                        </div>
                    ) : null}
                    {/* Contrato r4 do WarehouseMap (agente do mapa): edição
                        INLINE via mode; recorte de área no desenho via
                        areaFilter; seleção persistente + onSelect p/ drawer;
                        placeRequest/onPlaceResolved p/ posicionamento
                        obrigatório; onRackCreated após generate-rack REAL.
                        `overlay` segue alimentando o modo status. */}
                    <WarehouseMap
                        mode={mapEditMode ? 'edit' : 'status'}
                        layout={mapLayout}
                        onLayoutChange={setMapLayout}
                        overlay={mapRows}
                        areaFilter={selectedAreaId}
                        selection={selection}
                        onSelect={handleMapSelect}
                        fitHeight={mapFitHeight}
                        onRackCreated={handleRackCreated}
                        placeRequest={placeRequest}
                        onPlaceResolved={resolvePlacement}
                    />
                    <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.status_hint')}</p>
                </>
            )}
        </section>
    );

    return (
        <div className="space-y-4">
            <PageHeader
                title={label('wms.locations.title')}
                subtitle={label('wms.locations.description')}
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="secondary" onClick={() => setCreateOpen(true)}>
                            <PlusCircle size={18} className="mr-2" />
                            {label('wms.locations.new_location')}
                        </Button>
                        <Button variant="secondary" onClick={() => setBulkOpen(true)}>
                            <PencilSimple size={18} className="mr-2" />
                            {label('wms.locations.bulk_edit')}
                        </Button>
                        <Button variant="secondary" onClick={() => setLabelsOpen(true)}>
                            <Tag size={18} className="mr-2" />
                            {label('wms.locations.print_labels')}
                        </Button>
                    </div>
                }
            />

            {/* Filtro 1 (Área) + filtro 2 (modo) — os selects alimentam o GET e
                valem para Tabela/Cascata (o mapa desenha o layout; o recorte
                dele é o filtro de área). */}
            <div className="space-y-3 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    {areaChipButtons}
                    {viewToggle}
                </div>
                {view !== 'map' ? (
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="w-full md:w-44">
                            <Select
                                label={label('wms.field.rack')}
                                value={filters.rack}
                                onChange={(event) => setFilter('rack', event.target.value)}
                            >
                                <option value="">{label('wms.locations.all_racks')}</option>
                                {rackOptions.map((rack) => (
                                    <option key={rack} value={rack}>
                                        {`${label('wms.field.rack')} ${rack}`}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="w-full md:w-52">
                            <Select
                                label={label('wms.field.type')}
                                value={filters.type}
                                onChange={(event) => setFilter('type', event.target.value)}
                            >
                                <option value="">Todos os tipos</option>
                                {[
                                    ...BuildLocationTypeOptions(label),
                                    { value: VIRTUAL_LOCATION_TYPE, label: label('wms.location_type.VIRTUAL') },
                                ].map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="w-full md:w-44">
                            <Select
                                label={label('wms.field.status')}
                                value={filters.status}
                                onChange={(event) => setFilter('status', event.target.value)}
                            >
                                <option value="">Todos os status</option>
                                {BuildLocationStatusOptions(label).map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </div>
                ) : null}
            </div>

            {placementBanner}

            {/* Layout de DUAS colunas: modo escolhido à esquerda; DRAWER
                PERMANENTE à direita (coluna fixa ~360px, nunca overlay). */}
            <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch">
                <div className="min-w-0 flex-1 space-y-2">
                    {view !== 'map' ? truncatedNotice : null}
                    {view === 'table' ? tableContent : view === 'cascade' ? cascadeContent : mapContent}
                </div>
                <aside className="w-full shrink-0 xl:w-[360px]">
                    <div className="h-full xl:sticky xl:top-4">
                        <LocationInfoPanel
                            target={panelTarget}
                            label={label}
                            onClear={clearSelection}
                            onSelectLocation={selectLocationRow}
                            onShowOnMap={() => setView('map')}
                            onEdit={(row) => setEditingLocation(row)}
                        />
                    </div>
                </aside>
            </div>

            <LocationCreateModal
                isOpen={isCreateOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={reloadAll}
                onRequestPlacement={handleRequestPlacement}
            />
            <BulkLocationEditModal
                isOpen={isBulkOpen}
                onClose={() => setBulkOpen(false)}
                onApplied={reloadAll}
                rackOptions={rackOptions}
                initialRack={filters.rack}
            />
            <LocationLabelsModal
                isOpen={isLabelsOpen}
                onClose={() => setLabelsOpen(false)}
                rackOptions={rackOptions}
                initialRack=""
            />
            <LocationEditModal
                location={editingLocation}
                onClose={() => setEditingLocation(null)}
                onSaved={reloadAll}
            />
        </div>
    );
};

export default WarehouseLocationsPage;
